import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { openModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Panel from 'components/Panel';
import Preloader from 'components/Preloader';
import NothingBox from 'components/NothingBox';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import FeedRationCard from 'components/FeedRationCard';
// endpoints
import {
  getFeedRations,
  deleteFeedRation,
  disableFeedRation,
  enableFeedRation,
  updateFeedRationsSequence,
} from 'endpoints/admin/feedRations';
// constants
import { feedRationsPath } from './constants';
// utils
import classnames from 'classnames/bind';
import { showToastrMessage } from 'utils';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { reorder } from './helper';
// styles
import styles from './FeedRations.module.scss';

const cn = classnames.bind(styles);

const FeedRations = ({ openModal }) => {
  const [rations, setRations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    getFeedRations()
      .then(setRations)
      .catch(toastResponseErrors)
      .finally(() => setIsLoading(false));
  }, []);

  const replaceRation = (updated) => {
    setRations((prev) => prev.map((ration) => (ration.id === updated.id ? updated : ration)));
  };

  const toggleStatus = (ration) => {
    const request = ration.active ? disableFeedRation : enableFeedRation;
    setIsLoading(true);
    request(ration.id)
      .then((updated) => {
        replaceRation(updated);
        showToastrMessage('component.toastr.status.changed');
      })
      .catch(toastResponseErrors)
      .finally(() => setIsLoading(false));
  };

  const removeRation = (ration) => {
    setIsLoading(true);
    deleteFeedRation(ration.id)
      .then(() => {
        setRations((prev) => prev.filter(({ id }) => id !== ration.id));
        showToastrMessage('component.toastr.feedRation.deleted');
      })
      .catch(toastResponseErrors)
      .finally(() => setIsLoading(false));
  };

  const confirmDelete = (ration) => {
    openModal(
      <ConfirmationModal
        title={<FormattedMessage id="component.modal.deleteFeedRation.title" />}
        actionBtnLabel={<FormattedMessage id="component.modal.deleteFeedRation.confirm" />}
        actionBtnProps={{ red: true }}
        handleConfirm={() => removeRation(ration)}
      >
        <FormattedMessage
          id="component.modal.deleteFeedRation.text"
          tagName="p"
          values={{ name: <strong>{ration.name}</strong> }}
        />
      </ConfirmationModal>
    );
  };

  const onDragEnd = ({ source, destination }) => {
    if (!destination || destination.index === source.index) return;
    const previous = rations;
    const reordered = reorder(rations, source.index, destination.index);
    setRations(reordered);
    updateFeedRationsSequence(reordered.map(({ id }) => id))
      .then((saved) => {
        setRations(saved);
        showToastrMessage('component.toastr.feedRation.sequenceUpdated');
      })
      .catch((errors) => {
        setRations(previous);
        toastResponseErrors(errors);
      });
  };

  return (
    <section className="small-12 column">
      <Panel>
        <Panel.Heading title={<FormattedMessage id="general.feedRationSetup" />}>
          <Link to={`${feedRationsPath}/create`} className="button small primary wider">
            <FormattedMessage id="container.admin.feedRations.newRation" />
          </Link>
        </Panel.Heading>
        <Panel.Body className={cn('feed-rations-body')}>
          <Preloader isActive={isLoading} />

          {!!rations.length && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="feed-rations">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {rations.map((ration, index) => (
                      <Draggable
                        key={ration.id}
                        draggableId={String(ration.id)}
                        index={index}
                        isDragDisabled={isLoading}
                      >
                        {(draggableProvided, draggableSnapshot) => (
                          <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                            <FeedRationCard
                              ration={ration}
                              dragHandleProps={draggableProvided.dragHandleProps}
                              isDragging={draggableSnapshot.isDragging}
                              onToggleStatus={toggleStatus}
                              onDelete={confirmDelete}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          <NothingBox itemsName="feedRations" display={!rations.length} isLoading={isLoading}>
            <FormattedMessage tagName="h1" id="component.nothingBox.noFeedRations" />
            <FormattedMessage tagName="p" id="component.nothingBox.addFeedRation" />
            <Link to={`${feedRationsPath}/create`} className="button primary mt-10">
              <FormattedMessage id="container.admin.feedRations.newRation" />
            </Link>
          </NothingBox>
        </Panel.Body>
      </Panel>
    </section>
  );
};

FeedRations.propTypes = {
  openModal: T.func.isRequired,
};

export default connect(null, { openModal })(FeedRations);
